import React, { useState, useEffect } from 'react';
import { Lock, ArrowRight, CheckCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../supabase/config';
import { authService } from '../../services/authService';
import Logo from '../../components/common/Logo';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
        setChecking(false);
      }
    });

    // Also check if there's already an active session (event fired before mount)
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSessionReady(true);
      }
      setChecking(false);
    };

    // URL hash'teki token'lari Supabase'e islettir
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      // Supabase PKCE flow: hash'teki tokenlari parse et
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      if (accessToken && refreshToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          .then(({ error }) => {
            if (!error) {
              setSessionReady(true);
            } else {
              setError('Baglanti gecersiz veya suresi dolmus. Lutfen tekrar deneyin.');
            }
            setChecking(false);
          });
        return () => subscription.unsubscribe();
      }
    }

    checkSession();
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Sifre en az 6 karakter olmali.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Sifreler eslesmiyor.');
      return;
    }

    setLoading(true);
    try {
      await authService.updatePassword(password);
      setSuccess(true);
      // Logout so user logs in with new password
      await supabase.auth.signOut();
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message || 'Sifre guncellenirken bir hata olustu.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', padding: '2rem' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 32, height: 32, border: '3px solid rgba(111, 78, 55, 0.1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', padding: '2rem' }}>
        <div style={{ textAlign: 'center', maxWidth: 480, padding: '3.5rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 32, boxShadow: 'var(--shadow-md)' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem', textDecoration: 'none' }}>
            <Logo size={64} />
            <span style={{ fontSize: '2.5rem', fontWeight: 600, fontFamily: 'var(--font-logo)', position: 'relative', top: '3px', letterSpacing: '-0.02em', color: 'var(--text-main)' }}>ugra</span>
          </Link>
          <h2 style={{ color: 'var(--text-main)', marginBottom: '0.75rem', fontWeight: 800 }}>Baglanti Gecersiz</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            Sifre sifirlama baglantisinin suresi dolmus veya gecersiz. Lutfen yeni bir sifirlama isteginde bulunun.
          </p>
          <Link to="/forgot-password" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'white', background: 'var(--primary)', textDecoration: 'none', fontWeight: 700, fontSize: '0.9375rem', padding: '12px 24px', borderRadius: 14 }}>
            Tekrar Dene <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)', padding: '2rem', backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(111, 78, 55, 0.05) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(111, 78, 55, 0.05) 0%, transparent 40%)' }}>
      <div style={{ width: '100%', maxWidth: 480, padding: '3.5rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 32, boxShadow: 'var(--shadow-md)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, transparent, var(--primary), transparent)' }} />

        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem', textDecoration: 'none' }}>
            <Logo size={64} />
            <span style={{ fontSize: '2.5rem', fontWeight: 600, fontFamily: 'var(--font-logo)', position: 'relative', top: '3px', letterSpacing: '-0.02em', color: 'var(--text-main)' }}>ugra</span>
          </Link>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-main)', marginBottom: '0.75rem', letterSpacing: '-0.02em' }}>Yeni Sifre Belirle</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6 }}>Yeni sifrenizi asagiya girin.</p>
        </div>

        {success ? (
          <div style={{ textAlign: 'center', padding: '2rem', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
            <CheckCircle size={48} color="#16a34a" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#16a34a', marginBottom: '0.5rem', fontWeight: 700 }}>Sifreniz Guncellendi</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Giris sayfasina yonlendiriliyorsunuz...</p>
          </div>
        ) : (
          <>
            {error && (
              <div style={{ background: '#fff1f2', color: '#e11d48', padding: '1rem', borderRadius: 12, marginBottom: '2rem', fontSize: '0.875rem', fontWeight: 500, textAlign: 'center', border: '1px solid #ffe4e6' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '0.625rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Yeni Sifre</label>
                <Lock style={{ position: 'absolute', left: 16, top: 42, color: 'var(--text-muted)' }} size={20} />
                <input
                  type="password"
                  placeholder="En az 6 karakter"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%', padding: '0 16px 0 48px', height: 54, borderRadius: 14, border: '1.5px solid var(--border)', background: 'var(--bg-main)', fontSize: '0.9375rem', color: 'var(--text-main)' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: '0.625rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Sifre Tekrar</label>
                <Lock style={{ position: 'absolute', left: 16, top: 42, color: 'var(--text-muted)' }} size={20} />
                <input
                  type="password"
                  placeholder="Sifrenizi tekrar girin"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ width: '100%', padding: '0 16px 0 48px', height: 54, borderRadius: 14, border: '1.5px solid var(--border)', background: 'var(--bg-main)', fontSize: '0.9375rem', color: 'var(--text-main)' }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: '1rem', fontWeight: 700, marginTop: '2.5rem', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 14, cursor: 'pointer', boxShadow: '0 4px 12px rgba(111, 78, 55, 0.15)', opacity: loading ? 0.6 : 1 }}
              >
                {loading ? 'Guncelleniyor...' : 'Sifreyi Guncelle'}
                {!loading && <ArrowRight size={20} />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
